<template>
  <div>
    <h2>{{ title }}</h2>
    <loading :active.sync="isLoading" :is-full-page="true"></loading>
    <template>
      <v-system-bar class="branch-data" v-if="!sorting">
        <v-btn
          v-if="canWrite"
          rounded
          outlined
          color="tcdc_color"
          @click="startSorting">
          <v-icon small left>fas fa-arrows-alt-v</v-icon>
          Sort order
        </v-btn>
        <v-spacer></v-spacer>
        <div v-on:keypress.enter.prevent="getBranchTable(1)">
          <input-search
            :model="search" @update="searchInput"/>
        </div>
        <circle-button
          v-if="canWrite"
          @click.native="clearBranchData" />
      </v-system-bar>
      <!-- โหมดจัดลำดับ: ลากแถวเพื่อเรียงใหม่ แล้วกด Save -->
      <v-system-bar class="branch-data" v-else>
        <span>Drag rows to reorder branches, then press Save. ALL BRANCHES always stays first.</span>
        <v-spacer></v-spacer>
        <v-btn rounded class="mr-3" @click="cancelSorting" :disabled="savingOrder">Cancel</v-btn>
        <v-btn rounded color="tcdc_color" class="white--text" @click="saveSorting" :loading="savingOrder">Save</v-btn>
      </v-system-bar>
    </template>
    <!-- ตารางจัดลำดับ: แสดงทุกสาขาในหน้าเดียว ALL BRANCHES อยู่แถวแรกและลากไม่ได้ -->
    <template v-if="sorting">
      <v-simple-table class="branch-data">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-center" style="width: 60px"></th>
              <th class="text-center">Branch</th>
              <th class="text-center">Active</th>
              <th class="text-center">Order</th>
            </tr>
          </thead>
          <tbody v-if="fixedFirstBranch">
            <tr>
              <td align="center"><v-icon small color="grey lighten-1">fas fa-lock</v-icon></td>
              <td>{{ fixedFirstBranch.branch_name_th }}</td>
              <td align="center">{{ Number(fixedFirstBranch.active) === 1 ? 'Yes' : 'No' }}</td>
              <td>{{ fixedFirstBranch.orders }}</td>
            </tr>
          </tbody>
          <draggable v-model="sortList" tag="tbody" handle=".drag-handle" ghost-class="sort-ghost" :disabled="savingOrder">
            <tr v-for="(item, index) in sortList" :key="item.branch_id">
              <td align="center" class="drag-handle"><v-icon small>fas fa-grip-vertical</v-icon></td>
              <td>{{ item.branch_name_th }}</td>
              <td align="center">{{ Number(item.active) === 1 ? 'Yes' : 'No' }}</td>
              <td>
                {{ index + 1 }}
                <span v-if="Number(item.orders) !== index + 1" class="grey--text">(was {{ item.orders }})</span>
              </td>
            </tr>
          </draggable>
        </template>
      </v-simple-table>
    </template>
    <template v-else>
      <v-simple-table class="branch-data">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-center" v-for="header in headers" :key="header.id">
                {{ header.text }}
                <template v-if="header.icon">
                  <a @click="sortDataTable">
                    <v-icon class="fas fa-caret-down"
                            v-if="sort == 'desc'"
                            style="font-size: 12px"></v-icon>
                    <v-icon class="fas fa-caret-up"
                            v-if="sort == 'asc'"
                            style="font-size: 12px"></v-icon>
                  </a>
                </template>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in dataTable" :key="item.branch_id">
              <td align="center">{{((dataPaginate.current_page-1)*10)+(index+1)}}</td>
              <td>{{ item.branch_name_th }}</td>
              <td>{{ item.updated_at | moment("DD-MM-YYYY") }}</td>
              <td>{{ item.orders }}</td>
              <td>
                <a v-if="item.branch_id === 0">
                  <div style="text-align: center">
                  <v-icon class="fas fa-pencil-alt" style="font-size: 20px;"></v-icon>
                  </div>
                </a>
                <a @click="showBranch(item.branch_id)" v-else>
                  <icon-pencil />
                </a>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </template>
    <paginate v-if="!sorting"
              :length="dataPaginate.last_page"
              :page="dataPaginate.current_page"
              @update="changePage">
    </paginate>
  </div>
</template>

<script>
import IconPencil from "../../../components/Icon/icon-pencil";
import CircleButton from "../../../components/Button/CircleButton";
import InputTextField from "../../../components/Input/InputTextField";
import InputSearch from "../../../components/Input/InputSearch";
import Paginate from "../../../components/Layout/Paginate";
import {permissionMainBranch, role} from "../../../util/Permission";
import Loading from 'vue-loading-overlay';
import Swal from "sweetalert";
import draggable from "vuedraggable";

// ALL BRANCHES ต้องอยู่ลำดับแรกเสมอ (หน้าเว็บตัดรายการแรกทิ้งโดยถือว่าเป็น ALL BRANCHES)
const ALL_BRANCHES_ID = 0;

export default {
  name: "MainBranch",
  components: {Paginate, InputSearch, InputTextField, CircleButton, IconPencil, Loading, draggable },
  created() {
    this.permission = role;
    this.getBranchTable(1);
  },
  computed:{
    permissionMainBranch,
    canWrite() {
      return !!(this.permissionMainBranch && this.permission && this.permissionMainBranch.permission_id === this.permission.readWrite);
    }
  },
  data() {
    return {
      permission : null,
      isLoading : false,
      sorting: false,
      savingOrder: false,
      fixedFirstBranch: null,
      sortList: [],
      title: "Main Branch",
      search : '',
      sort : 'asc',
      headers: [
        { id: 1, text: "No." ,'icon' : null},
        { id: 2, text: "Branch" ,'icon' : null},
        { id: 3, text: "Update date" ,'icon' : null},
        { id: 4, text: "Order" ,'icon' : true},
        { id: 5, text: "Edit" ,'icon' : null}
      ],
      dataTable: [],
      dataPaginate: {
        current_page: 1,
        from: 1,
        last_page: 1,
        per_page: 1,
        total: 1,
        to: 1
      }
    };
  },
  methods: {
    goToBranchEdit() {
      this.$router.push("/branch/edit");
    },
    getBranchTable(page) {
      this.isLoading = true;
      this.$store.dispatch("Branch/getBranch", {
        page,
        search : this.search,
        sort : this.sort,
      }).then(res => {
        if (res.response_code == 200) {
          this.dataTable = res.result.data;
          this.dataPaginate = {
            current_page: res.result.current_page,
            from: res.result.from,
            last_page: res.result.last_page,
            per_page: res.result.per_page,
            total: res.result.total,
            to: res.result.to
          }
        } else {
          Swal(res.response_msg);
        }
        this.isLoading = false;
      });
    },
    showBranch(id) {
      this.isLoading = true;
      this.$store.dispatch("Branch/showBranch", id).then(res => {
        if (res.response_code == 200) {
          this.$store.dispatch("Branch/setFormBranch", res.result).then(() => {
            this.goToBranchEdit();
          });
        } else {
          Swal(res.response_msg);
        }
        this.isLoading = false;
      });
    },
    clearBranchData() {
      this.$store.dispatch("Branch/clearFormData").then(() => {
        this.goToBranchEdit();
      });
    },
    async changePage(val){
      this.isLoading = true;
      this.dataPaginate.current_page = val;
      await this.getBranchTable(this.dataPaginate.current_page);
      this.isLoading = false;
    },
    searchInput(val){
      this.search = val;
    },
    sortDataTable(){
      this.sort = this.sort == 'desc' ? 'asc' : 'desc';
      console.log(this.sort);
      this.getBranchTable(1);
    },
    // ดึงทุกสาขาเรียงตาม order (ไม่แบ่งหน้า) แยก ALL BRANCHES ออกจากรายการที่ลากได้
    async loadSortList() {
      const branches = await this.$store.dispatch("Branch/getBranchAll");
      const sorted = [...(branches || [])].sort((a, b) =>
        (Number(a.orders) || 0) - (Number(b.orders) || 0) || a.branch_id - b.branch_id
      );
      this.fixedFirstBranch = sorted.find(item => item.branch_id === ALL_BRANCHES_ID) || null;
      this.sortList = sorted.filter(item => item.branch_id !== ALL_BRANCHES_ID);
    },
    async startSorting() {
      this.isLoading = true;
      try {
        await this.loadSortList();
        this.sorting = true;
      } catch (e) {
        Swal("", "Load branches failed!", "error");
      }
      this.isLoading = false;
    },
    cancelSorting() {
      this.sorting = false;
      this.sortList = [];
      this.fixedFirstBranch = null;
    },
    // บันทึกลำดับใหม่: ALL BRANCHES = 0, สาขาอื่น = 1..n ตามแถวที่ลาก
    // ส่งเฉพาะสาขาที่ order เปลี่ยน โดยใช้ข้อมูลล่าสุดจาก API เพื่อไม่ให้ทับค่าที่คนอื่นเพิ่งแก้
    async saveSorting() {
      this.savingOrder = true;
      try {
        const latest = await this.$store.dispatch("Branch/getBranchAll");
        const latestById = {};
        (latest || []).forEach(item => { latestById[item.branch_id] = item; });

        const changes = [];
        const missing = [];
        this.sortList.forEach((item, index) => {
          const newOrder = index + 1;
          const current = latestById[item.branch_id];
          if (!current) {
            missing.push(item.branch_name_th);
            return;
          }
          if (Number(current.orders) !== newOrder) {
            changes.push({ ...current, orders: newOrder });
          }
        });
        const allBranches = latestById[ALL_BRANCHES_ID];
        if (allBranches && Number(allBranches.orders) !== 0) {
          changes.push({ ...allBranches, orders: 0 });
        }

        // API ต้องมีชื่อ TH/EN และ tag ถ้าสาขาไหนขาด ไม่ส่งเลยสักสาขา เพื่อไม่ให้ลำดับบันทึกไปแค่บางส่วน
        const invalid = changes.filter(item => !item.branch_name_th || !item.branch_name_en || !item.tag_name);
        if (missing.length > 0 || invalid.length > 0) {
          const names = [...missing, ...invalid.map(item => item.branch_name_th || item.branch_name_en || item.branch_id)];
          Swal("", "Cannot save order. Please check these branches: " + names.join(", "), "error");
          this.savingOrder = false;
          return;
        }

        const failed = [];
        for (const item of changes) {
          try {
            const res = await this.$store.dispatch("Branch/saveBranch", { id: item.branch_id, data: item });
            if (!res || res.response_code != 200) failed.push(item.branch_name_th);
          } catch (e) {
            failed.push(item.branch_name_th);
          }
        }

        if (failed.length > 0) {
          Swal("", "Some branches were not saved: " + failed.join(", ") + ". Please check the order and save again.", "error");
          await this.loadSortList();
        } else {
          Swal(changes.length > 0 ? "Save order success." : "Order not changed.");
          this.cancelSorting();
          this.getBranchTable(1);
        }
      } catch (e) {
        Swal("", "Save order failed!", "error");
      }
      this.savingOrder = false;
    }
  }
};
</script>

<style scoped>
@import "../../../assets/css/table.css";
/*.branch-data {*/
/*  background-color: transparent;*/
/*  margin: 20px;*/
/*}*/
</style>
<style>
.drag-handle {
  cursor: move;
}
/* ตำแหน่งที่แถวจะถูกวาง: พื้นเขียวอ่อน กรอบเขียว */
.sort-ghost {
  outline: 2px dashed #2E8037;
  outline-offset: -2px;
}
.sort-ghost td {
  background: #edf5d1 !important;
  color: #2E8037;
}
.theme--light.v-system-bar {
  background-color: transparent !important;
  color: rgba(0, 0, 0, 0.6);
}
</style>
